import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    CreditCard, Download, CheckCircle2, Zap, Calendar,
    ShieldCheck, FileText, Loader2, MessageCircle, Rocket, ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { useBilling } from '@/contexts/BillingContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { PackageCard } from '@/components/billing/PackageCard';
import { VISIBILITY_PACKAGES } from '@/components/billing/constants';

// --- Interfaces & Types (Inferred from usage) ---
interface Invoice {
    id: string;
    number: string;
    date: string;
    amount: number;
    status: 'paid' | 'pending' | 'failed';
    invoice_url: string | null;
}

// --- Helper Functions ---
const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR'
    }).format(amount);
};

const getPlanLabel = (planId?: string) => {
    if (!planId) return '';
    switch (planId) {
        case 'visibility': return 'Clip Visibility';
        case 'standard': return 'Clip Standard';
        case 'pro': return 'Clip Pro';
        case 'ai': return 'Clip AI';
        case 'enterprise': return 'Clip Enterprise';
        default: return planId;
    }
};

const Billing: React.FC = () => {
    const { t, i18n } = useTranslation();

    // Hook Integration
    const { invoices, subscription, loading, createCustomerPortalSession } = useBilling();
    const { visibility, organization, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = React.useState<'plan' | 'visibility'>(location.state?.initialTab || 'plan');
    const [buying, setBuying] = React.useState<string | null>(null);

    const purchaseVisibility = async (lookupKey: string, days: number, weight: number) => {
        if (!organization?.id || !user?.email) {
            console.error("Missing organization or user params");
            return;
        }

        setBuying(lookupKey);
        try {
            const { data, error } = await supabase.functions.invoke('create-visibility-checkout', {
                body: {
                    shop_id: organization.id,
                    email: user.email,
                    lookup_key: lookupKey,
                    days: days,
                    weight: weight
                }
            });

            if (error) throw error;
            if (data?.url) {
                window.location.href = data.url;
            }
        } catch (error) {
            console.error('Error creating visibility checkout:', error);
            alert(t('common.error_occurred'));
        } finally {
            setBuying(null);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    const tabContentVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
        exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
    };

    // Helper for WhatsApp
    const handleContactSupport = () => {
        window.open(`https://wa.me/447593288804`, '_blank');
    };

    // Logic helper for invoice status UI style
    const getInvoiceStatusStyle = (status: string) => {
        switch (status) {
            case 'paid':
                return 'bg-green-100 text-green-700';
            case 'pending':
                return 'bg-yellow-100 text-yellow-700';
            case 'failed':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const getInvoiceStatusText = (status: string) => {
        switch (status) {
            case 'paid': return t('billing.status.paid');
            case 'pending': return t('billing.status.pending');
            case 'failed': return t('billing.status.failed');
            default: return status;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-clip-primary" />
            </div>
        );
    }

    // Handle default/free subscription
    const effectiveSub = subscription || {
        status: 'active',
        plan_id: 'free',
        amount: 0,
        billing_cycle: 'month',
        current_period_end: new Date().toISOString(),
        cancel_at_period_end: false,
        payment_method: null
    };

    const isFree = effectiveSub.plan_id === 'free';

    const PLAN_FEATURES = {
        visibility: t('subscription.plans.visibility.features', { returnObjects: true }) as string[],
        standard: [
            t('billing.features.staff_limit'),
            t('billing.features.bookings_limit'),
            t('billing.features.unlimited_emails'),
            t('billing.features.advanced_agenda'),
            t('billing.features.support')
        ],
        pro: t('subscription.plans.pro.features', { returnObjects: true }) as string[],
        ai: t('subscription.plans.ai.features', { returnObjects: true }) as string[],
        enterprise: t('subscription.plans.enterprise.features', { returnObjects: true }) as string[]
    };

    const currentFeatures = (effectiveSub.plan_id === 'ai' || effectiveSub.plan_id === 'enterprise')
        ? PLAN_FEATURES[effectiveSub.plan_id as 'ai' | 'enterprise']
        : (effectiveSub.plan_id?.includes('pro') || effectiveSub.plan_id === 'professional')
            ? PLAN_FEATURES.pro
            : (effectiveSub.plan_id === 'standard' ? PLAN_FEATURES.standard : PLAN_FEATURES.visibility);

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-5xl mx-auto space-y-8 pb-12"
        >
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">{t('billing.title')}</h1>
                    <p className="text-gray-500 mt-1">{t('billing.subtitle')}</p>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-gray-400 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-100">
                    <ShieldCheck size={14} className="text-clip-500" />
                    {t('billing.secure_payment')}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Left Column: Main Content (Tabs) */}
                <motion.div variants={itemVariants} className="lg:col-span-2 space-y-8">

                    {/* Tabs */}
                    <div className="bg-white rounded-2xl p-1.5 border border-gray-100 shadow-sm flex w-full gap-1">
                        <button
                            onClick={() => setActiveTab('plan')}
                            className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === 'plan'
                                ? 'bg-clip-500 text-white shadow-md shadow-clip-200'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                        >
                            <CreditCard size={16} />
                            {t('billing.tabs.plan', 'Piano')}
                        </button>
                        <button
                            onClick={() => setActiveTab('visibility')}
                            className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === 'visibility'
                                ? 'bg-clip-500 text-white shadow-md shadow-clip-200'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                        >
                            <Zap size={16} />
                            {t('billing.tabs.visibility', 'Visibilità')}
                        </button>
                    </div>

                    {/* PLAN TAB */}
                    {activeTab === 'plan' && (
                        <motion.div
                            key="plan"
                            variants={tabContentVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden relative">
                                {/* <div className="absolute top-0 right-0 p-6 opacity-10 pointer-events-none">
                                    <CreditCard size={120} className="text-clip-500" />
                                </div> */}

                                <div className="p-8">
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <span className={`inline-block px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wider mb-2 ${effectiveSub.status === 'active' ? 'bg-clip-50 text-clip-600' :
                                                effectiveSub.status === 'trialing' ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'
                                                }`}>
                                                {effectiveSub.status === 'active' ? t('billing.active_plan') : effectiveSub.status}
                                            </span>
                                            <h2 className="text-3xl font-bold text-gray-900">
                                                {getPlanLabel(effectiveSub.plan_id)}
                                            </h2>
                                            <p className="text-gray-500 mt-1">
                                                {effectiveSub.cancel_at_period_end
                                                    ? `${t('billing.ends_on')} ${format(new Date(effectiveSub.current_period_end), 'dd/MM/yyyy')}`
                                                    : t('billing.plan_desc')
                                                }
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <span className="block text-4xl font-bold text-gray-900">
                                                {formatAmount(effectiveSub.amount)}
                                            </span>
                                            {!isFree && <span className="text-gray-400 text-sm font-medium">
                                                / {effectiveSub.billing_cycle === 'month' ? t('billing.month') : t('billing.year')}
                                            </span>}
                                        </div>
                                    </div>

                                    {/* Features Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                                        {currentFeatures.map((feature, i) => (
                                            <div key={i} className="flex items-center gap-3 text-sm text-gray-600">
                                                <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                                                {feature}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Usage Bar Mockup */}
                                    {/* <div className="bg-gray-50 rounded-2xl p-4 mb-8 border border-gray-100">
                                        <div className="flex justify-between text-sm mb-2">
                                            <span className="font-bold text-gray-700">{t('billing.usage_sms')}</span>
                                            <span className="text-gray-500">850 / 1000</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div className="bg-clip-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                                        </div>
                                    </div> */}

                                    <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-100">
                                        {isFree ? (
                                            <button
                                                onClick={() => navigate('/subscription')}
                                                className="flex-1 px-6 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-all shadow-lg flex items-center justify-center gap-2"
                                            >
                                                <Zap size={18} fill="currentColor" />
                                                {t('billing.upgrade_plan')}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={createCustomerPortalSession}
                                                className="flex-1 px-6 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-all shadow-lg flex items-center justify-center gap-2"
                                            >
                                                <ExternalLink size={18} />
                                                {t('billing.manage_plan')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* VISIBILITY TAB */}
                    {activeTab === 'visibility' && (
                        <motion.div
                            key="visibility"
                            variants={tabContentVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden relative"
                        >

                            <div className="p-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('billing.visibility_title')}</h2>
                                <p className="text-gray-500 mb-8">{t('billing.visibility_desc')}</p>

                                {visibility && new Date(visibility.end_date) > new Date() ? (
                                    // ACTIVE VISIBILITY PLAN
                                    <div className="space-y-6">
                                        <div className="bg-green-50 rounded-2xl p-6 border border-green-100 mb-6">
                                            <div className="flex items-center gap-4 mb-4">
                                                <div className="bg-green-100 p-3 rounded-full text-green-600">
                                                    <Zap size={24} fill="currentColor" />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-lg text-green-900">
                                                        {t('billing.active_boost')}
                                                    </h3>
                                                    <p className="text-green-700 text-sm">
                                                        {t('billing.expires_on')} {format(new Date(visibility.end_date), 'dd MMMM yyyy', { locale: i18n.language === 'it' ? it : enUS })}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                                <div
                                                    className="bg-green-500 h-2 rounded-full transition-all duration-1000"
                                                    style={{
                                                        width: `${Math.max(5, Math.min(100, Math.round(((Date.now() - new Date(visibility.updated_at || Date.now()).getTime()) /
                                                            (new Date(visibility.end_date).getTime() - new Date(visibility.updated_at || Date.now()).getTime()) * 100) / 5) * 5))}%`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>

                                        {(() => {
                                            const availableUpgrades = VISIBILITY_PACKAGES.filter(pkg => pkg.weight > (visibility.priority_weight || 0));

                                            if (availableUpgrades.length === 0) return null;

                                            return (
                                                <>
                                                    <h3 className="font-bold text-gray-900 mb-4">{t('billing.upgrade_boost_title')}</h3>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {availableUpgrades.map(pkg => (
                                                            <PackageCard
                                                                key={pkg.id}
                                                                pkg={pkg}
                                                                isUpgrade={true}
                                                                purchaseVisibility={purchaseVisibility}
                                                                buying={buying}
                                                            />
                                                        ))}
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                ) : (
                                    // NO ACTIVE VISIBILITY - SHOW ALL PLANS
                                    <div className="space-y-6">
                                        {VISIBILITY_PACKAGES.map(pkg => (
                                            <PackageCard
                                                key={pkg.id}
                                                pkg={pkg}
                                                purchaseVisibility={purchaseVisibility}
                                                buying={buying}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}


                    {/* Invoices List Content */}
                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 mt-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <FileText className="text-clip-500" size={20} /> {t('billing.invoices_history')}
                        </h3>
                        {/* Existing Invoice List code */}
                        <div className="space-y-1">
                            {invoices.length === 0 ? (
                                <p className="text-center text-gray-500 py-4">{t('billing.no_invoices')}</p>
                            ) : (
                                invoices.map((inv: Invoice) => (
                                    <div key={inv.id} className="group flex items-center justify-between p-4 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 group-hover:bg-white group-hover:shadow-sm transition-all">
                                                <FileText size={18} />
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-900 text-sm">
                                                    {format(new Date(inv.date), 'dd MMM yyyy', { locale: i18n.language === 'it' ? it : enUS })}
                                                </p>
                                                <p className="text-xs text-gray-400">#{inv.number}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-6">
                                            <span className="text-sm font-bold text-gray-900">
                                                {formatAmount(inv.amount)}
                                            </span>
                                            <span className={`hidden sm:inline-block px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wide ${getInvoiceStatusStyle(inv.status)}`}>
                                                {getInvoiceStatusText(inv.status)}
                                            </span>
                                            <button
                                                className="p-2 text-gray-400 hover:text-clip-500 hover:bg-clip-50 rounded-lg transition-all disabled:opacity-50"
                                                title="Scarica PDF"
                                                disabled={!inv.invoice_url}
                                                onClick={() => inv.invoice_url && window.open(inv.invoice_url, '_blank')}
                                            >
                                                <Download size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        {invoices.length > 5 && (
                            <button className="w-full mt-4 py-3 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors">
                                {t('billing.view_all_invoices')}
                            </button>
                        )}
                    </div>

                </motion.div >

                {/* Right Column: Payment Method */}
                < motion.div variants={itemVariants} className="space-y-6" >

                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-6">{t('billing.payment_method')}</h3>

                        {effectiveSub.payment_method ? (
                            /* Visual Credit Card */
                            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white shadow-xl shadow-gray-200 mb-6 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-6 opacity-20">
                                    <CreditCard size={100} />
                                </div>
                                <div className="relative z-10">
                                    <div className="mb-8">
                                        <div className="w-10 h-6 rounded bg-yellow-200/80 mb-2"></div>
                                    </div>
                                    <div className="space-y-6">
                                        <p className="font-mono text-xl tracking-widest text-shadow">
                                            •••• •••• •••• {effectiveSub.payment_method.last4}
                                        </p>
                                        <div className="flex justify-between items-end">
                                            <div>
                                                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{t('billing.expires')}</p>
                                                <p className="font-mono text-sm font-bold">
                                                    {effectiveSub.payment_method.exp_month}/{effectiveSub.payment_method.exp_year}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold italic text-lg uppercase">
                                                    {effectiveSub.payment_method.brand}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* No Payment Card State */
                            <div className="bg-gray-50 rounded-2xl p-6 text-center border-2 border-dashed border-gray-200">
                                <CreditCard size={48} className="mx-auto text-gray-300 mb-3" />
                                <p className="text-gray-500 text-sm mb-4">
                                    {t('billing.no_payment_method', 'No payment method')}
                                </p>
                            </div>
                        )}

                        {!isFree && <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl border border-orange-100 mt-6">
                            <Calendar size={18} className="text-orange-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-bold text-orange-900">{t('billing.next_renewal')}</p>
                                <p className="text-xs text-orange-700 mt-0.5">
                                    {t('billing.renewal_text')} <span className="font-bold">
                                        {format(new Date(effectiveSub.current_period_end), 'dd MMMM yyyy', { locale: i18n.language === 'it' ? it : enUS })}
                                    </span>.
                                </p>
                            </div>
                        </div>}

                        {/* Bottone aggiorna metodo di pagamento */}
                        {/* <button
                            onClick={createCustomerPortalSession}
                            className="w-full py-3 border border-gray-200 rounded-xl font-bold text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 mt-6"
                        >
                            <CreditCard size={16} /> {t('billing.update_method')}
                        </button> */}
                    </div>

                    {/* Updated Support Section to use WhatsApp */}
                    <div className="bg-gradient-to-br from-clip-500 to-orange-600 rounded-3xl p-6 text-white shadow-lg shadow-clip-200">
                        <h3 className="font-bold text-lg mb-2">{t('billing.help_title')}</h3>
                        <p className="text-clip-100 text-sm mb-4 leading-relaxed">
                            {t('billing.help_text')}
                        </p>
                        <button
                            onClick={handleContactSupport}
                            className="w-full py-3 bg-white text-clip-600 font-bold rounded-xl text-sm hover:bg-clip-50 transition-colors flex items-center justify-center gap-2"
                        >
                            <MessageCircle size={18} /> Chat WhatsApp
                        </button>
                    </div>

                </motion.div >
            </div >
        </motion.div >
    );
};

export default Billing;